import React, { useContext, useEffect, useState } from 'react';
import { Grid } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import TabsUsers from '../../../components/common/TabsUsers';
import Alert from '../../../components/common/Alert';
import InformationUser from './InformationUser';
import Schedule from './Schedule';
import SetSessions from './SetSessions';
import UsersAPI from '../user-api';
import PillarAPI from '../../PillarContainer/pillar-api';
import { UserContext } from './userContext';
import ColumnSkeleton from '../../../components/common/Skeletons';
import { DatosEmprendimiento } from './InformationUser/Mentees/DatosEmprendimiento';
import { HistorialActividad } from './InformationUser/Mentees/HistorialActividad';
import Restrictions from './Restrictions';
import ProfessionalExperience from './ProfessionalExperience';

const UserEditForm = () => {
  const location = useLocation();
  const navigationState = location.state || {};
  const searchParams = new URLSearchParams(location.search);
  const idUser =
    navigationState.idUser || searchParams.get('userId') || undefined;
  const { clientId, ref } = navigationState;
  const initialTab = searchParams.get('tab') || undefined;
  const [loading, setLoading] = useState(true);
  const [reloadVersion, setReloadVersion] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [messageAlert, setMessageAlert] = useState({
    message: '',
    type: '',
  });

  const [state, dispatch] = useContext(UserContext);

  const roleType = state.user?.role?.type;
  const canManageFutureSessions = ['mentee', 'none'].includes(roleType);
  const isMentee = roleType === 'mentee';
  const isStandardUser = !['admin', 'manager'].includes(roleType);
  const tabs = [{ title: 'Información usuario' }];
  const content = [
    <InformationUser
      key="information"
      onRoleChanged={() => setReloadVersion((current) => current + 1)}
      setShowAlert={setShowAlert}
      setMessageAlert={setMessageAlert}
    />,
  ];

  if (roleType === 'mentor') {
    tabs.push({
      key: 'professional-experience',
      title: 'Experiencia profesional',
    });
    content.push(
      <ProfessionalExperience
        key="professional-experience"
        userId={state.user.id}
      />
    );
    tabs.push({ title: 'Horarios' });
    content.push(<Schedule key="schedule" />);
  }

  if (canManageFutureSessions) {
    if (isMentee) {
      tabs.push({ title: 'Datos emprendimiento' });
      content.push(
        <DatosEmprendimiento
          key="company"
          setShowAlert={setShowAlert}
          setMessageAlert={setMessageAlert}
        />
      );
    }

    tabs.push({ title: 'Reuniones futuras' });
    content.push(<SetSessions key="future-sessions" />);
  }

  if (isStandardUser) {
    tabs.push({ title: 'Historial de actividad' });
    content.push(<HistorialActividad key="activity" />);
    tabs.push({ key: 'restrictions', title: 'Restricciones' });
    content.push(
      <Restrictions
        key="restrictions"
        setShowAlert={setShowAlert}
        setMessageAlert={setMessageAlert}
      />
    );
  }

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    const loadUser = async () => {
      const [userResponse, pillarsResponse] = await Promise.allSettled([
        UsersAPI.getUserById(idUser),
        PillarAPI.fetchActivePillars(),
      ]);

      if (
        userResponse.status !== 'fulfilled' ||
        pillarsResponse.status !== 'fulfilled'
      ) {
        if (!isMounted) {
          return;
        }

        setMessageAlert({
          message: 'No fue posible cargar la información del usuario.',
          type: 'error',
        });
        setShowAlert(true);
        return;
      }

      const { extra, extraData, ...rest } = userResponse.value.data;
      const loadedRole = rest.role || {
        id: null,
        name: 'Sin rol',
        type: 'none',
      };
      const loadedRoleType = loadedRole.type;
      const loadedCanManageFutureSessions = ['mentee', 'none'].includes(
        loadedRoleType
      );
      const [mentorsResponse, scheduleResponse, sessionsResponse] =
        await Promise.allSettled([
          loadedCanManageFutureSessions
            ? UsersAPI.getMentors(clientId, ref)
            : Promise.resolve({ data: [] }),
          loadedRoleType === 'mentor'
            ? UsersAPI.getMentorCalendar(idUser)
            : Promise.resolve({ data: [] }),
          !['admin', 'manager'].includes(loadedRoleType)
            ? UsersAPI.getSessions(idUser)
            : Promise.resolve({ data: [] }),
        ]);

      if (!isMounted) {
        return;
      }

      const userInfo = {
        ...extra,
        ...extraData,
        ...rest,
        role: loadedRole,
        pillars: Array.isArray(rest.pillars) ? rest.pillars : [],
        sessions:
          sessionsResponse.status === 'fulfilled'
            ? sessionsResponse.value.data
            : [],
      };

      dispatch({
        type: 'FETCH_INITIAL_STATE',
        payload: {
          user: userInfo,
          pillars: pillarsResponse.value.data,
          mentors:
            mentorsResponse.status === 'fulfilled'
              ? mentorsResponse.value.data
              : [],
          schedule:
            scheduleResponse.status === 'fulfilled'
              ? scheduleResponse.value?.data || []
              : [],
        },
      });
    };

    loadUser()
      .catch(() => {
        if (isMounted) {
          setMessageAlert({
            message: 'No fue posible cargar la información del usuario.',
            type: 'error',
          });
          setShowAlert(true);
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [clientId, dispatch, idUser, ref, reloadVersion]);

  useEffect(() => {
    if (!showAlert) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setShowAlert(false);
    }, 10000);

    return () => {
      clearTimeout(timer);
    };
  }, [showAlert]);

  return (
    <div style={{ minHeight: 'calc(100vh - 88px)' }}>
      {loading ? (
        <Grid container className="user-container" spacing={4}>
          <ColumnSkeleton rows={8} />
          <ColumnSkeleton rows={8} />
          <ColumnSkeleton rows={8} />
        </Grid>
      ) : !state.user?.role ? (
        <Grid container className="user-container">
          <Grid item xs={12}>
            {showAlert && (
              <Alert type={messageAlert.type}>{messageAlert.message}</Alert>
            )}
          </Grid>
        </Grid>
      ) : (
        <Grid container className="user-container">
          <Grid item xs={12}>
            {showAlert && (
              <Alert type={messageAlert.type}>{messageAlert.message}</Alert>
            )}
            <Grid container className="user-container__edit" direction="column">
              <Grid item className="tabs-edit-user">
                <TabsUsers
                  tabs={tabs}
                  content={content}
                  initialTab={initialTab}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default UserEditForm;
